import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Container,
  FormSelect,
  InputGroup,
  Row,
} from "shards-react";
import { getHistoricGraphData } from "../backend/BinAlgData";
import { ISO8601_week_no, parseDate } from "../backend/common";
import CardProbabilities from "../components/common/CardProbabilities";
import { Loading } from "../components/common/Loader";
import PageTitle from "../components/common/PageTitle";
import WeeklyDatePicker from "../components/common/WeeklyDatePicker";
import { Animals, VACUNO_MACHO_KEY, VACUNO_HEMBRA_KEY, FRISON_MACHO_KEY } from "../utils/constants";

const moment = require("moment");

// ─── Helpers de color / tendencia ────────────────────────────────────────────

const getTrendColor = (trend) => {
  switch (trend) {
    case "up":       return "rgb(15, 178, 0)";
    case "down":     return "rgb(179, 48, 48)";
    case "maintain": return "rgb(214, 159, 7)";
    default:         return "rgb(142, 142, 142)";
  }
};

const getTrendIcon = (trend) => {
  switch (trend) {
    case "up":       return "⬆";
    case "down":     return "⬇";
    case "maintain": return "〓";
    default:         return "–";
  }
};

// ─── Presets de botones de acceso rápido ─────────────────────────────────────

const QUICK_PRESETS = [
  {
    label: "Machos",
    key: VACUNO_MACHO_KEY,
    sex: Animals.bovineMale.sex,
    weight: Animals.bovineMale.weight[2], // 281/320KG
    fatType: Animals.bovineMale.fatType[2], // U
    type: Animals.bovineMale.type,
  },
  {
    label: "Hembras",
    key: VACUNO_HEMBRA_KEY,
    sex: Animals.bovineFemale.sex,
    weight: Animals.bovineFemale.weight[3], // 261/300KG
    fatType: Animals.bovineFemale.fatType[2], // U
    type: Animals.bovineFemale.type,
  },
  {
    label: "Frisón",
    key: FRISON_MACHO_KEY,
    sex: Animals.frison.sex,
    weight: Animals.frison.weight[1], // <220KG
    fatType: Animals.frison.fatType[1], // O
    type: Animals.frison.type,
  },
];

// ─── Tarjeta de precio individual ────────────────────────────────────────────

const PriceCard = ({ label, value, color }) => (
  <div style={{ textAlign: "center" }}>
    <span
      style={{
        display: "block",
        fontSize: 12,
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: 1,
        marginBottom: 8,
      }}
    >
      {label}
    </span>
    <div
      style={{
        padding: "16px 28px",
        borderRadius: 12,
        border: `2px solid ${color || "#ddd"}`,
        backgroundColor: color
          ? `${color.replace("rgb", "rgba").replace(")", ", 0.06)")}`
          : "#f9f9f9",
        minWidth: 130,
      }}
    >
      <span
        style={{
          fontSize: 40,
          fontWeight: "bold",
          color: color || "#333",
        }}
      >
        {value !== undefined && value !== null && !isNaN(value)
          ? `${Number(value).toFixed(2)} €`
          : "–"}
      </span>
    </div>
  </div>
);

// ─── Vista principal ──────────────────────────────────────────────────────────

export const PresentationView = () => {
  const [selectedWeek, setSelectedWeek] = useState(moment().startOf("isoWeek"));

  const [animalType, setAnimalType] = useState({
    key: VACUNO_MACHO_KEY,
    sex: Animals.bovineMale.sex,
    weight: Animals.bovineMale.weight[2],
    fatType: Animals.bovineMale.fatType[2],
    type: Animals.bovineMale.type,
  });

  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState([]);
  const [prices, setPrices] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [priceVariations, setPriceVariations] = useState([]);
  const [prediction4Weeks, setPrediction4Weeks] = useState([]);
  const [diff4Weeks, setDiff4Weeks] = useState([]);

  const applyPreset = (preset) => {
    setAnimalType({
      key: preset.key,
      sex: preset.sex,
      weight: preset.weight,
      fatType: preset.fatType,
      type: preset.type,
    });
  };

  const getData = () => {
    setLoading(true);
    const fromDate = selectedWeek.clone().subtract(5, "week");
    const toDate = selectedWeek.clone().add(5, "week");

    getHistoricGraphData(
      {
        sex: animalType.sex,
        weight: animalType.weight,
        fatType: animalType.fatType,
        type: animalType.type,
        from: fromDate.format("DD-MM-YYYY"),
        to: toDate.format("DD-MM-YYYY"),
      },
      (result) => {
        const priceResult = result.binefar;
        const variationResult = result.variation;
        const predictionResult = result.predicted;
        const prediction4WeeksResult = result.predicted4weeks;

        let datesR = [];
        let date = fromDate.clone();
        while (date.isSameOrBefore(toDate)) {
          datesR.push(date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"));
          date.add(1, "week");
        }

        const priceR = datesR.map(
          (d) => priceResult.find((v) => v.date.standard === d)?.price ?? undefined
        );
        const variationR = datesR.map(
          (d) => variationResult.find((v) => v.date.standard === d)?.variation ?? undefined
        );
        const predictionR = datesR.map(
          (d) => predictionResult.find((v) => v.date.standard === d)?.price ?? undefined
        );
        const prediction4WeeksR = datesR.map(
          (d) =>
            prediction4WeeksResult.find((v) => v.date.standard === d)?.probabilities ?? undefined
        );

        const priceVariationR = variationR.map((val, index) => {
          const diff = val + priceR[index];
          return isNaN(diff) ? undefined : diff.toFixed(2);
        });
        priceVariationR.pop();

        setDates(datesR);
        setPrices(priceR);
        setPredictions(predictionR);
        setPriceVariations([
          isNaN(priceR[0] + variationR[0])
            ? undefined
            : (priceR[0] + variationR[0]).toFixed(2),
          ...priceVariationR,
        ]);
        setPrediction4Weeks(prediction4WeeksR);
        setLoading(false);
      }
    );
  };

  useEffect(() => { getData(); }, [animalType, selectedWeek]);

  useEffect(() => {
    const diff4WeeksR = prices.map((price, index) => {
      const price4WeeksAhead = prices[index + 4];
      if (price === undefined || price4WeeksAhead === undefined) return undefined;
      const diff = (price4WeeksAhead - price).toFixed(2);
      if (diff < -0.03) return "down";
      if (diff > 0.03) return "up";
      return "maintain";
    });
    setDiff4Weeks(diff4WeeksR);
  }, [prices, prediction4Weeks]);

  // ── Índices clave ──────────────────────────────────────────────────────────
  const selectedIdx = dates.findIndex(
    (d) => d === selectedWeek.clone().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
  );
  const lastWeekIdx = selectedIdx > 0 ? selectedIdx - 1 : -1;
  const currentIdx = selectedIdx;

  const lastWeekReal       = prices?.[lastWeekIdx];
  const lastWeekPrediction = predictions?.[lastWeekIdx];
  const lastWeekTendencia  = priceVariations?.[lastWeekIdx];

  const lastWeekRealTrend = (() => {
    if (lastWeekIdx < 1) return undefined;
    const prev = prices?.[lastWeekIdx - 1];
    const curr = prices?.[lastWeekIdx];
    if (prev === undefined || curr === undefined) return undefined;
    const diff = curr - prev;
    if (diff < -0.03) return "down";
    if (diff > 0.03) return "up";
    return "maintain";
  })();

  const currentPrediction = predictions?.[currentIdx];
  const currentTendencia  = priceVariations?.[currentIdx];
  const current4Weeks     = prediction4Weeks?.[currentIdx];
  const current4WeeksDiff = diff4Weeks?.[currentIdx];

  const maxProbsIndex = current4Weeks
    ? Object.keys(current4Weeks).reduce((a, b) =>
        current4Weeks[a] > current4Weeks[b] ? a : b
      )
    : null;

  const currentWeekNo = currentIdx >= 0 && dates[currentIdx]
    ? ISO8601_week_no(new Date(dates[currentIdx]))
    : null;
  const currentWeekPlus4No = currentIdx >= 0 && dates[currentIdx]
    ? ISO8601_week_no(new Date(new Date(dates[currentIdx]).getTime() + 4 * 7 * 24 * 3600 * 1000))
    : null;

  // ── Helpers semana pasada predicción a 4 semanas ──────────────────────────
  const weekNoOf    = (dateStr) => dateStr ? ISO8601_week_no(new Date(dateStr)) : null;
  const weekPlus4Of = (dateStr) =>
    dateStr
      ? ISO8601_week_no(new Date(new Date(dateStr).getTime() + 4 * 7 * 24 * 3600 * 1000))
      : null;

  // Solo la primera card del pasado (la más antigua de las 4 semanas previas)
  const firstPastIdx = selectedIdx >= 4 ? selectedIdx - 4 : 0;
  const firstPastProbs    = prediction4Weeks[firstPastIdx];
  const firstPastRealTrend = diff4Weeks[firstPastIdx];
  const firstPastMaxProb  = firstPastProbs
    ? Object.keys(firstPastProbs).reduce((a, b) => firstPastProbs[a] > firstPastProbs[b] ? a : b)
    : null;
  const firstPastWeekNo    = weekNoOf(dates[firstPastIdx]);
  const firstPastWeekPlus4 = weekPlus4Of(dates[firstPastIdx]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="main-content-container px-4 pb-4">
      <PageTitle
        title="Presentación de Predicciones"
        md="12"
        className="ml-sm-auto mr-sm-auto"
      />

      {/* ── Controles ── */}
      <Row
        className="mb-4 align-items-end"
        style={{ gap: 12, paddingLeft: 15, flexWrap: "wrap" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Semana</label>
          <WeeklyDatePicker
            date={selectedWeek}
            changeDate={(d) => setSelectedWeek(d.startOf("isoWeek"))}
          />
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Animal</label>
          <InputGroup>
            <FormSelect
              value={animalType.key}
              onChange={(e) => {
                const animal = Animals[e.target.value];
                setAnimalType({
                  key: e.target.value,
                  sex: animal.sex,
                  weight: Object.values(animal.weight)[0],
                  fatType: Object.values(animal.fatType)[0],
                  type: animal.type,
                });
              }}
            >
              {Object.entries(Animals).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.name}
                </option>
              ))}
            </FormSelect>
          </InputGroup>
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Peso</label>
          <InputGroup>
            <FormSelect
              value={animalType.weight}
              onChange={(e) => setAnimalType({ ...animalType, weight: e.target.value })}
            >
              {Object.entries(Animals[animalType.key].weight).map(([k, v]) => (
                <option key={k} value={v}>{v}</option>
              ))}
            </FormSelect>
          </InputGroup>
        </div>
        <div style={{ minWidth: 120 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Clasificación</label>
          <InputGroup>
            <FormSelect
              value={animalType.fatType}
              onChange={(e) => setAnimalType({ ...animalType, fatType: e.target.value })}
            >
              {Object.values(Animals[animalType.key].fatType).map((ft) => (
                <option key={ft} value={ft}>{ft}</option>
              ))}
            </FormSelect>
          </InputGroup>
        </div>

        {/* ── Botones de acceso rápido ── */}
        <div
          style={{
            borderLeft: "1px solid #ddd",
            paddingLeft: 16,
            marginLeft: 4,
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: 6,
              fontWeight: 600,
              fontSize: 11,
              color: "#999",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Acceso rápido
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            {QUICK_PRESETS.map((preset) => {
              const isActive =
                animalType.key === preset.key &&
                animalType.weight === preset.weight &&
                animalType.fatType === preset.fatType;
              return (
                <button
                  key={preset.label}
                  onClick={() => applyPreset(preset)}
                  style={{
                    padding: "7px 18px",
                    borderRadius: 6,
                    border: `2px solid ${isActive ? "#007bff" : "#ccc"}`,
                    backgroundColor: isActive ? "#007bff" : "#fff",
                    color: isActive ? "#fff" : "#555",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
      </Row>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Loading />
        </div>
      ) : (
        <Row style={{ gap: 20, margin: 0 }}>

          {/* ═══════════════════════════════════════════════════════
              PANEL IZQUIERDO — Semana pasada
          ═══════════════════════════════════════════════════════ */}
          <Col sm="12" lg="5" style={{ padding: 0 }}>
            <Card
              style={{
                boxShadow: "0 2px 12px rgba(100,110,140,.15)",
                padding: "24px",
                height: "100%",
              }}
            >
              {/* Cabecera semana pasada */}
              <div style={{ marginBottom: 20 }}>
                <h5 style={{ margin: 0, color: "#444", fontWeight: 700 }}>
                  Semana pasada
                </h5>
                {lastWeekIdx >= 0 && dates[lastWeekIdx] && (
                  <span style={{ fontSize: 13, color: "#888" }}>
                    {parseDate(dates[lastWeekIdx])}
                  </span>
                )}
              </div>

              {/* Tarjetas Real / Tendencia / Predicción — mismo tamaño que la derecha */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  justifyContent: "center",
                  marginBottom: 24,
                }}
              >
                <PriceCard
                  label="Real"
                  value={lastWeekReal}
                  color={getTrendColor(lastWeekRealTrend)}
                />
                <PriceCard
                  label="Tendencia"
                  value={lastWeekTendencia}
                  color="rgba(19,196,24,1)"
                />
                <PriceCard
                  label="Predicción"
                  value={lastWeekPrediction}
                  color="rgba(255,65,105,1)"
                />
              </div>

              {/* Diferencia predicción vs real */}
              {lastWeekReal !== undefined && lastWeekPrediction !== undefined && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 20,
                    padding: "8px 16px",
                    borderRadius: 8,
                    backgroundColor: "#f7f7f7",
                    fontSize: 13,
                    color: "#666",
                  }}
                >
                  Desviación predicción:{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      color:
                        Math.abs(lastWeekPrediction - lastWeekReal) < 0.03
                          ? getTrendColor("up")
                          : getTrendColor("down"),
                    }}
                  >
                    {(lastWeekPrediction - lastWeekReal >= 0 ? "+" : "") +
                      (lastWeekPrediction - lastWeekReal).toFixed(3)}{" "}
                    €
                  </span>
                </div>
              )}

              {/* Divider */}
              <hr style={{ borderColor: "#eee", margin: "0 0 24px 0" }} />

              {/* ── Predicción a 4 semanas (solo primera card, estilo igual que derecha) ── */}
              <div>
                <p
                  style={{
                    fontSize: 12,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 16,
                    fontWeight: 600,
                  }}
                >
                  Predicción a 4 semanas
                </p>

                {firstPastProbs ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 32,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* CardProbabilities grande — maxProbsIndex apunta a lo que OCURRIÓ realmente */}
                    <div style={{ transform: "scale(1.4)", transformOrigin: "left center", marginLeft: 10 }}>
                      <CardProbabilities
                        currentWeek={firstPastWeekNo}
                        currentWeekPlus4={firstPastWeekPlus4}
                        probs={firstPastProbs}
                        maxProbsIndex={firstPastRealTrend}
                        pricesDiff4Weeks={firstPastRealTrend}
                      />
                    </div>

                    {/* Descripción verbal — basada en lo real, no en el máximo prob */}
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div
                        style={{
                          padding: "14px 18px",
                          borderRadius: 10,
                          backgroundColor: `${getTrendColor(firstPastRealTrend)
                            .replace("rgb", "rgba")
                            .replace(")", ", 0.08)")}`,
                          border: `2px solid ${getTrendColor(firstPastRealTrend)}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 16,
                            fontWeight: "bold",
                            color: getTrendColor(firstPastRealTrend),
                          }}
                        >
                          {getTrendIcon(firstPastRealTrend)}{" "}
                          {firstPastRealTrend === "up"
                            ? "Subió realmente"
                            : firstPastRealTrend === "down"
                            ? "Bajó realmente"
                            : "Se mantuvo realmente"}
                        </span>
                        <br />
                        <span style={{ fontSize: 13, color: "#666" }}>
                          Semana {firstPastWeekNo}º → {firstPastWeekPlus4}º · predicción:{" "}
                          {firstPastProbs[firstPastMaxProb]
                            ? `${(firstPastProbs[firstPastMaxProb] * 100).toFixed(0)}% ${firstPastMaxProb === "up" ? "subida" : firstPastMaxProb === "down" ? "bajada" : "estable"}`
                            : "–"}
                        </span>
                      </div>

                      {/* Barra de probabilidades — resalta la opción real */}
                      <div style={{ marginTop: 14 }}>
                        {[
                          { key: "up", label: "Subida", icon: "⬆" },
                          { key: "maintain", label: "Estable", icon: "〓" },
                          { key: "down", label: "Bajada", icon: "⬇" },
                        ].map(({ key, label, icon }) => {
                          const pct = firstPastProbs[key]
                            ? (firstPastProbs[key] * 100).toFixed(0)
                            : 0;
                          const isReal = key === firstPastRealTrend;
                          return (
                            <div key={key} style={{ marginBottom: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 13,
                                  marginBottom: 3,
                                  fontWeight: isReal ? "bold" : "normal",
                                  color: isReal ? getTrendColor(key) : "#888",
                                }}
                              >
                                <span>
                                  {icon} {label}
                                  {isReal && (
                                    <span style={{ fontSize: 11, marginLeft: 5, opacity: 0.8 }}>
                                      ← real
                                    </span>
                                  )}
                                </span>
                                <span>{pct}%</span>
                              </div>
                              <div
                                style={{
                                  height: 6,
                                  backgroundColor: "#eee",
                                  borderRadius: 3,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    backgroundColor: getTrendColor(key),
                                    borderRadius: 3,
                                    transition: "width 0.5s ease",
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 30,
                      color: "#bbb",
                      fontSize: 15,
                    }}
                  >
                    Sin predicción a 4 semanas disponible
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* ═══════════════════════════════════════════════════════
              PANEL DERECHO — Semana actual / predicción a 4 semanas
          ═══════════════════════════════════════════════════════ */}
          <Col sm="12" lg="6" style={{ padding: 0 }}>
            <Card
              style={{
                boxShadow: "0 2px 12px rgba(100,110,140,.15)",
                padding: "24px",
                height: "100%",
              }}
            >
              {/* Cabecera semana actual */}
              <div style={{ marginBottom: 20 }}>
                <h5 style={{ margin: 0, color: "#444", fontWeight: 700 }}>
                  Semana actual
                </h5>
                {currentIdx >= 0 && dates[currentIdx] && (
                  <span style={{ fontSize: 13, color: "#888" }}>
                    {parseDate(dates[currentIdx])}
                  </span>
                )}
              </div>

              {/* Tendencia y predicción de la semana actual */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  justifyContent: "center",
                  marginBottom: 24,
                }}
              >
                <PriceCard
                  label="Tendencia"
                  value={currentTendencia}
                  color="rgba(19,196,24,1)"
                />
                <PriceCard
                  label="Predicción IA"
                  value={currentPrediction}
                  color="rgba(255,65,105,1)"
                />
              </div>

              {/* Espaciador para alinear con el bloque de desviación del panel izquierdo */}
              <div style={{ height: 56 }} />

              {/* Divider */}
              <hr style={{ borderColor: "#eee", margin: "0 0 24px 0" }} />

              {/* Predicción a 4 semanas */}
              <div>
                <p
                  style={{
                    fontSize: 12,
                    color: "#888",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    marginBottom: 16,
                    fontWeight: 600,
                  }}
                >
                  Predicción a 4 semanas
                </p>

                {current4Weeks ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 32,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* CardProbabilities grande */}
                    <div style={{ transform: "scale(1.4)", transformOrigin: "left center", marginLeft: 10 }}>
                      <CardProbabilities
                        currentWeek={currentWeekNo}
                        currentWeekPlus4={currentWeekPlus4No}
                        probs={current4Weeks}
                        maxProbsIndex={maxProbsIndex}
                        pricesDiff4Weeks={current4WeeksDiff}
                      />
                    </div>

                    {/* Descripción verbal */}
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div
                        style={{
                          padding: "14px 18px",
                          borderRadius: 10,
                          backgroundColor: `${getTrendColor(maxProbsIndex)
                            .replace("rgb", "rgba")
                            .replace(")", ", 0.08)")}`,
                          border: `2px solid ${getTrendColor(maxProbsIndex)}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 16,
                            fontWeight: "bold",
                            color: getTrendColor(maxProbsIndex),
                          }}
                        >
                          {getTrendIcon(maxProbsIndex)}{" "}
                          {maxProbsIndex === "up"
                            ? "Tendencia alcista"
                            : maxProbsIndex === "down"
                            ? "Tendencia bajista"
                            : "Precio estable"}
                        </span>
                        <br />
                        <span style={{ fontSize: 13, color: "#666" }}>
                          Semana {currentWeekNo}º → {currentWeekPlus4No}º (
                          {(current4Weeks[maxProbsIndex] * 100).toFixed(0)}% probabilidad)
                        </span>
                      </div>

                      {/* Barra de probabilidades */}
                      <div style={{ marginTop: 14 }}>
                        {[
                          { key: "up", label: "Subida", icon: "⬆" },
                          { key: "maintain", label: "Estable", icon: "〓" },
                          { key: "down", label: "Bajada", icon: "⬇" },
                        ].map(({ key, label, icon }) => {
                          const pct = current4Weeks[key]
                            ? (current4Weeks[key] * 100).toFixed(0)
                            : 0;
                          const isMax = key === maxProbsIndex;
                          return (
                            <div key={key} style={{ marginBottom: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 13,
                                  marginBottom: 3,
                                  fontWeight: isMax ? "bold" : "normal",
                                  color: isMax ? getTrendColor(key) : "#888",
                                }}
                              >
                                <span>{icon} {label}</span>
                                <span>{pct}%</span>
                              </div>
                              <div
                                style={{
                                  height: 6,
                                  backgroundColor: "#eee",
                                  borderRadius: 3,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    backgroundColor: getTrendColor(key),
                                    borderRadius: 3,
                                    transition: "width 0.5s ease",
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 30,
                      color: "#bbb",
                      fontSize: 15,
                    }}
                  >
                    Sin predicción a 4 semanas disponible
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PresentationView;